import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent OAuth, AuthCallback handles it; skip /me here.
    // Check both hash (spec) and query string (mobile fallback).
    if (typeof window !== "undefined") {
      const hash = window.location.hash || "";
      const search = window.location.search || "";
      if (hash.includes("session_id=") || search.includes("session_id=")) {
        setLoading(false);
        return;
      }
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (email, password, recaptcha_token, remember = false) => {
    const { data } = await api.post("/auth/login", { email, password, recaptcha_token, remember });
    if (data.session_token) setToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name, recaptcha_token) => {
    const { data } = await api.post("/auth/register", { email, password, name, recaptcha_token });
    if (data.session_token) setToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const exchangeGoogle = async (session_id) => {
    const { data } = await api.post("/auth/google/session", { session_id });
    if (data.session_token) setToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("Logout request failed:", e); }
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh, login, register, logout, exchangeGoogle }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
